/**
 * Linear Regression
 *
 * Plots the endpoint of the linear regression line over `period` bars.
 * Uses least-squares method for line fitting.
 *
 * Category: Trend | Pane: Overlay
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { getSource } from "./sma";

export const linregIndicator: IndicatorDefinition = {
  id: "linreg",
  name: "Linear Regression",
  shortName: "LINREG",
  category: "trend",
  pane: "overlay",
  params: [
    { key: "period", label: "Period", type: "number", default: 14, min: 2, max: 500 },
    { key: "source", label: "Source", type: "select", default: "close", options: [
      { label: "Close", value: "close" }, { label: "Open", value: "open" },
      { label: "High", value: "high" }, { label: "Low", value: "low" },
    ]},
    { key: "color", label: "Color", type: "color", default: "#795548" },
  ],
  plots: [
    { key: "linreg", label: "LinReg", color: "#795548", style: "line", lineWidth: 2 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period, source = "close" } = params;
    const src = getSource(candles, source);
    const data: { time: number; value: number }[] = [];

    for (let i = period - 1; i < src.length; i++) {
      let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
      for (let j = 0; j < period; j++) {
        sumX += j;
        sumY += src[i - period + 1 + j];
        sumXY += j * src[i - period + 1 + j];
        sumX2 += j * j;
      }
      const n = period;
      const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
      const intercept = (sumY - slope * sumX) / n;
      const value = intercept + slope * (period - 1);
      data.push({ time: candles[i].time, value });
    }

    return [{ key: "linreg", data }];
  },
};
